import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Grid,
  TextField,
  Typography,
  Backdrop,
  makeStyles,
} from "@material-ui/core";

import Autocomplete from "@material-ui/lab/Autocomplete";

import { useAlert } from "react-alert";

import {
  AuctionListLimit,
  GenerateReceipt,
} from "../../../adminModel/auctions";


/* =========================================================
   STYLES
========================================================= */

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },

  section: {
    backgroundColor: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: theme.spacing(3),
  },

  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: "#1f2937",
    marginBottom: theme.spacing(0.5),
  },

  sectionSubtitle: {
    fontSize: 13,
    color: "#6b7280",
    marginBottom: theme.spacing(3),
  },

  field: {
    marginBottom: theme.spacing(2.5),
  },

  lotNumberField: {
    "& .MuiInputBase-root": {
      fontSize: 14,
    },
  },

  documentSection: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: "1px solid #e5e7eb",
  },

  documentTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#374151",
    marginBottom: theme.spacing(1),
  },

  checkbox: {
    marginRight: theme.spacing(3),

    "& .MuiFormControlLabel-label": {
      fontSize: 14,
      color: "#374151",
    },
  },

  actionArea: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(2),
    borderTop: "1px solid #e5e7eb",
  },

  viewButton: {
    minWidth: 110,
    height: 40,
    textTransform: "none",
    fontWeight: 600,
  },

  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },

  helperText: {
    fontSize: 12,
    color: "#6b7280",
    marginTop: theme.spacing(0.75),
  },
}));


/* =========================================================
   COMPONENT
========================================================= */

const Views = ({ className, ...rest }) => {
  const classes = useStyles();
  const alert = useAlert();

  /* =======================================================
     STATE
  ======================================================= */

  const [loading, setLoading] = useState(false);

  const [value2, setValue2] = useState("");
  const [value3, setValue3] = useState("");

  const [lot, setlot] = useState([]);

  const [eventId, setEventId] = useState("");
  const [data, setData] = useState([]);

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [lotNumber, setLotNumber] = useState("");
  const [receiptNumber, setReceiptNumber] = useState("");
  const [eventName, setEventName] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const [selectedEvent, setSelectedEvent] = useState(null);

  const [geteventList, setEventList] = useState([]);


  /* =======================================================
     RESTORE RECEIPT DATA
  ======================================================= */

  useEffect(() => {
    const savedData = localStorage.getItem("receipt_data");

    if (!savedData) {
      return;
    }

    try {
      const parsed = JSON.parse(savedData);

      setEventName(parsed.eventName || "");
      setName(parsed.name || "");
      setLotNumber(parsed.lotNumber || "");
      setReceiptNumber(parsed.receiptNumber || "");
    } catch (error) {
      console.error("Failed to restore receipt data:", error);
    }
  }, []);


  /* =======================================================
     LOAD AUCTION LIST
  ======================================================= */

  useEffect(() => {
    async function fetchData() {
      setLoading(true);

      try {
        const results = await AuctionListLimit();

        if (results && results.data) {
          setEventList(results.data);
        } else {
          setEventList([]);
        }
      } catch (error) {
        console.error("Failed to fetch auction list:", error);
        setEventList([]);
      } finally {
        setLoading(false);
      }
    }

    fetchData().catch(console.error);
  }, []);


  /* =======================================================
     SELECT EVENT
  ======================================================= */

  const handlepick = (event, value) => {
    try {
      if (!value) {
        setEventId("");
        setEventName("");
        setSelectedEvent(null);
        return;
      }

      setEventName(value.auction_name || "");
      setEventId(value.id || "");
      setSelectedEvent(value);

    } catch (error) {
      setEventId("");
      setEventName("");
      setSelectedEvent(null);

      console.error("Failed to select event:", error);
    }
  };


  /* =======================================================
     VIEW / GENERATE RECEIPT
  ======================================================= */

  const ViewLot = async () => {
    localStorage.setItem("lotView", lot);
    localStorage.setItem("print2", value2);
    localStorage.setItem("print3", value3);

    const eventDate =
      selectedEvent && selectedEvent.date_start
        ? selectedEvent.date_start.split("T")[0]
        : "";

    const savedDataRaw =
      localStorage.getItem("receipt_data");

    let savedData = null;

    try {
      savedData = savedDataRaw
        ? JSON.parse(savedDataRaw)
        : null;
    } catch (error) {
      savedData = null;
    }

    const inputsMatch =
      savedData &&
      savedData.email === email &&
      savedData.name === name &&
      savedData.lotNumber === lotNumber;


    /* =====================================================
       EXISTING RECEIPT
    ===================================================== */

    if (
      inputsMatch &&
      savedData.receiptNumber
    ) {
      setReceiptNumber(savedData.receiptNumber);

      window.open(
        `/receipt/certificate/invoice/print/${eventName}/${name}/${eventId}/${savedData.receiptNumber}/${eventDate}`,
        "_blank"
      );

      return;
    }


    /* =====================================================
       GENERATE NEW RECEIPT
    ===================================================== */

    setLoading(true);
    setErrorMsg("");

    try {
      const response = await GenerateReceipt();

      if (response.success) {

        const newData = {
          eventName,
          name,
          lotNumber,
          receiptNumber: response.number,
          eventDate,
          email,
        };

        setReceiptNumber(response.number);

        localStorage.setItem(
          "receipt_data",
          JSON.stringify(newData)
        );


        /* =================================================
           VALIDATION
        ================================================= */

        if (
          eventId === "" ||
          lot.length === 0 ||
          name === "" ||
          (value2 === "" && value3 === "")
        ) {
          setEventId(
            eventId === ""
              ? true
              : eventId
          );

          setlot(
            lot.length === 0
              ? ""
              : lot
          );

          setName(
            name === ""
              ? true
              : name
          );

          alert.error("Required Field");

        } else {

          window.open(
            `/receipt/certificate/invoice/print/${eventName}/${name}/${eventId}/${response.number}/${eventDate}`,
            "_blank"
          );
        }

      } else {

        setErrorMsg(
          response.error ||
          "Unable to generate receipt."
        );

        alert.error(
          response.error ||
          "Unable to generate receipt."
        );
      }

    } catch (error) {

      console.error(
        "Failed to generate receipt:",
        error
      );

      setErrorMsg(
        "An unexpected error occurred."
      );

      alert.error(
        "An unexpected error occurred."
      );

    } finally {
      setLoading(false);
    }
  };


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div
      className={`${classes.root} ${
        className || ""
      }`}
      {...rest}
    >

      <Grid container spacing={3}>

        {/* =================================================
            LOT NUMBERS
        ================================================= */}

        <Grid item xs={12} md={5}>

          <Box className={classes.section}>

            <Typography
              className={classes.sectionTitle}
            >
              Lot Numbers
            </Typography>

            <Typography
              className={classes.sectionSubtitle}
            >
              Enter the lot numbers you want to
              include.
            </Typography>

            <TextField
              className={classes.lotNumberField}
              fullWidth
              multiline
              rows={12}
              variant="outlined"
              label="Enter Lot Numbers"
              placeholder={"Example:\n1\n2\n3"}
              value={lot || ""}
              onChange={(e) =>
                setlot(e.target.value)
              }
            />

            <Typography
              className={classes.helperText}
            >
              Enter one lot number per line.
            </Typography>

          </Box>

        </Grid>


        {/* =================================================
            DOCUMENT DETAILS
        ================================================= */}

        <Grid item xs={12} md={7}>

          <Box className={classes.section}>

            <Typography
              className={classes.sectionTitle}
            >
              Document Details
            </Typography>

            <Typography
              className={classes.sectionSubtitle}
            >
              Select the auction and document
              information.
            </Typography>


            {/* =============================================
                EVENT
            ============================================= */}

            <Box className={classes.field}>

              <Autocomplete
                fullWidth
                disablePortal
                options={geteventList}
                value={selectedEvent}
                getOptionLabel={(option) =>
                  option
                    ? option.auction_name || ""
                    : ""
                }
                onChange={handlepick}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Auction Event"
                    variant="outlined"
                    error={
                      eventId === true
                    }
                    helperText={
                      eventId === true
                        ? "Auction event is required"
                        : ""
                    }
                  />
                )}
              />

            </Box>


            {/* =============================================
                DOCUMENT TYPE
            ============================================= */}

            <Box className={classes.documentSection}>

              <Typography
                className={classes.documentTitle}
              >
                Document Type
              </Typography>

              <FormGroup row>

                <FormControlLabel
                  className={classes.checkbox}
                  control={
                    <Checkbox
                      color="primary"
                      checked={value2 === 2}
                      onChange={() =>
                        setValue2(
                          value2 === 2
                            ? ""
                            : 2
                        )
                      }
                    />
                  }
                  label="Certificate"
                />

                <FormControlLabel
                  className={classes.checkbox}
                  control={
                    <Checkbox
                      color="primary"
                      checked={value3 === 3}
                      onChange={() =>
                        setValue3(
                          value3 === 3
                            ? ""
                            : 3
                        )
                      }
                    />
                  }
                  label="Receipt"
                />

              </FormGroup>

            </Box>


            {/* =============================================
                CUSTOMER NAME
            ============================================= */}

            <Box
              className={classes.field}
              style={{
                marginTop: 24,
              }}
            >

              <TextField
                fullWidth
                variant="outlined"
                label="Customer Name"
                value={
                  name === true
                    ? ""
                    : name
                }
                error={name === true}
                helperText={
                  name === true
                    ? "Customer Name is required"
                    : ""
                }
                onChange={(e) =>
                  setName(
                    e.target.value
                  )
                }
              />

            </Box>


            {/* =============================================
                ACTION
            ============================================= */}

            <Box className={classes.actionArea}>

              <Button
                color="primary"
                variant="contained"
                className={classes.viewButton}
                onClick={ViewLot}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress
                    size={20}
                    color="inherit"
                  />
                ) : (
                  "View"
                )}
              </Button>

            </Box>

          </Box>

        </Grid>

      </Grid>


      {/* =================================================
          LOADING
      ================================================= */}

      <Backdrop
        className={classes.backdrop}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

    </div>
  );
};


/* =========================================================
   PROP TYPES
========================================================= */

Views.propTypes = {
  className: PropTypes.string,
};


export default Views;